(() => {
  const storageKey = "bizafrikaCataloguePreviewRfq";
  const pageSize = 24;

  const escapeHtml = (value) => String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");

  const safeHttpUrl = (value) => {
    try {
      const url = new URL(String(value), window.location.origin);
      return ["http:", "https:"].includes(url.protocol) ? escapeHtml(url.href) : "";
    } catch (_error) {
      return "";
    }
  };

  const buyer = window.BizAfrikaBuyerJourney;
  const loadQuote = () => buyer.read();
  const saveQuote = (items) => buyer.save(items);

  const uniqueValues = (values) => [...new Set(values.filter(Boolean))];

  const matchesProductQuery = (values, query) => {
    const searchable = values.filter(Boolean).join(" ").toLowerCase();
    return String(query || "").trim().toLowerCase().split(/\s+/).every((term) => searchable.includes(term));
  };

  const specification = (label, value) => value
    ? `<div><dt>${escapeHtml(label)}</dt><dd>${escapeHtml(value)}</dd></div>`
    : "";

  const customerFacingEatonStatus = (status) => ({
    "rfq-ready": "Quotation available",
    enrichment: "Specification confirmed on quotation",
    "availability-request": "Availability confirmed on quotation",
    "details-review": "Specification guidance available",
    legacy: "Alternative or replacement guidance",
  })[status] || "Technical guidance available";

  const customerFacingEatonCategory = (category) => ({
    "Needs classification": "Industrial electrical products",
    "Third-party / Buy-out Review": "Sourced electrical products",
    "Legacy / Obsolete Review": "Legacy product support",
  })[category] || category;

  const initializeCatalogue = async (root) => {
    if (root.dataset.initialized === "true") return;
    root.dataset.initialized = "true";
    const copy = JSON.parse(root.querySelector('[data-buyer-copy]')?.textContent || '{}');
    const text = (key, fallback) => copy[key] || fallback;
    const recovery = (message, brand, query, category = '', status = '') => `<div class="bizafrika-catalogue__empty"><p>${escapeHtml(message)}</p><a class="button-secondary" href="${escapeHtml(buyer.guidanceUrl({source: 'shop', brand, search: query, category, status}))}">${escapeHtml(text('send_search', 'Send this search for review'))}</a></div>`;

    const discoveryForm = root.querySelector("[data-discovery-search-form]");
    const discoverySearch = root.querySelector("[data-discovery-search]");
    const discoveryStatus = root.querySelector("[data-discovery-status]");
    const discoveryEmpty = root.querySelector("[data-discovery-empty]");
    const discoveryItems = [...root.querySelectorAll("[data-discovery-item]")];

    const applyDiscoverySearch = () => {
      if (!discoverySearch) return;
      const query = discoverySearch.value.trim().toLowerCase();
      let brandCount = 0;
      let categoryCount = 0;

      discoveryItems.forEach((item) => {
        const searchable = `${item.dataset.searchTerms || ""} ${item.textContent || ""}`.toLowerCase();
        const matches = matchesProductQuery([searchable], query);
        item.hidden = !matches;
        if (matches && item.dataset.discoveryKind === "brand") brandCount += 1;
        if (matches && item.dataset.discoveryKind === "category") categoryCount += 1;
      });

      const resultCount = brandCount + categoryCount;
      if (discoveryEmpty) discoveryEmpty.hidden = !query || resultCount > 0;
      const assistance = root.querySelector('[data-discovery-guidance]');
      if (assistance) assistance.href = buyer.guidanceUrl({ source: 'shop', search: discoverySearch.value });
      if (!discoveryStatus) return;
      discoveryStatus.textContent = query
        ? `${resultCount} result${resultCount === 1 ? "" : "s"}: ${categoryCount} categor${categoryCount === 1 ? "y" : "ies"} and ${brandCount} brand${brandCount === 1 ? "" : "s"}.`
        : "Search across 10 specialist brands, five product pathways and BizAfrika services.";
    };

    if (discoverySearch) {
      const initialQuery = new URLSearchParams(window.location.search).get("q") || "";
      discoverySearch.value = initialQuery;
      // Native GET submission reaches the same detailed product search as Home.
      discoveryForm?.addEventListener("reset", () => {
        window.setTimeout(applyDiscoverySearch, 0);
      });
      applyDiscoverySearch();
      discoverySearch.value = new URLSearchParams(window.location.search).get('item_search') || initialQuery;
    }

    if (!root.querySelector("[data-catalogue-tab]")) return;

    const syncPreviewBarOffset = () => {
      const previewBar = document.querySelector('#preview-bar-iframe, #PBarNextFrame, iframe[src*="preview_bar"], iframe[src*="preview-bar"]');
      const previewMode = window.Shopify?.previewMode === true;
      root.classList.toggle("has-shopify-preview-bar", previewMode || Boolean(previewBar));
    };
    syncPreviewBarOffset();
    window.setTimeout(syncPreviewBarOffset, 1000);
    window.setTimeout(syncPreviewBarOffset, 3000);

    const state = {
      quote: loadQuote(),
      dragflow: null,
      eaton: null,
      eatonVisible: pageSize,
    };

    const tabs = [...root.querySelectorAll("[data-catalogue-tab]")];
    const panels = [...root.querySelectorAll("[data-catalogue-panel]")];
    const dragflowGrid = root.querySelector("[data-dragflow-grid]");
    const dragflowCount = root.querySelector("[data-dragflow-count]");
    const dragflowSearch = root.querySelector("[data-dragflow-search]");
    const dragflowCategory = root.querySelector("[data-dragflow-category]");
    const eatonGrid = root.querySelector("[data-eaton-grid]");
    const eatonCount = root.querySelector("[data-eaton-count]");
    const eatonSearch = root.querySelector("[data-eaton-search]");
    const eatonCategory = root.querySelector("[data-eaton-category]");
    const eatonStatus = root.querySelector("[data-eaton-status]");
    const eatonLoadMore = root.querySelector("[data-eaton-load-more]");
    const rfqTray = root.querySelector("[data-rfq-tray]");
    const rfqToggle = root.querySelector("[data-rfq-toggle]");
    const rfqPanel = root.querySelector("[data-rfq-panel]");
    const rfqClose = root.querySelector("[data-rfq-close]");
    const rfqItems = root.querySelector("[data-rfq-items]");
    const rfqCount = root.querySelector("[data-rfq-count]");
    const rfqFeedback = root.querySelector("[data-rfq-feedback]");
    const rfqContactLink = root.querySelector("[data-rfq-contact]");
    const storageWarning = root.querySelector('[data-rfq-storage-warning]');

    const setTab = (name, updateHash = false) => {
      tabs.forEach((tab) => {
        const active = tab.dataset.catalogueTab === name;
        tab.classList.toggle("is-active", active);
        tab.setAttribute("aria-selected", String(active));
        tab.tabIndex = active ? 0 : -1;
      });
      panels.forEach((panel) => {
        panel.hidden = panel.dataset.cataloguePanel !== name;
      });
      if (updateHash) {
        const hash = name === "eaton" ? "#eaton-catalogue" : (name === "dragflow" ? "#dragflow-catalogue" : "#how-quotations-work");
        window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}${hash}`);
      }
    };

    tabs.forEach((tab, index) => {
      tab.addEventListener("click", () => setTab(tab.dataset.catalogueTab, true));
      tab.addEventListener("keydown", (event) => {
        if (!["ArrowLeft", "ArrowRight"].includes(event.key)) return;
        event.preventDefault();
        const direction = event.key === "ArrowRight" ? 1 : -1;
        const next = (index + direction + tabs.length) % tabs.length;
        tabs[next].focus();
        setTab(tabs[next].dataset.catalogueTab, true);
      });
    });

    root.querySelectorAll('a[href="#dragflow-catalogue"], a[href="#eaton-catalogue"]').forEach((link) => {
      link.addEventListener("click", (event) => {
        event.preventDefault();
        const isEaton = link.getAttribute("href") === "#eaton-catalogue";
        setTab(isEaton ? "eaton" : "dragflow", true);
        root.querySelector(isEaton ? "#eaton-catalogue" : "#dragflow-catalogue")?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    });

    if (window.location.hash === "#eaton-catalogue") setTab("eaton");
    if (window.location.hash === "#how-quotations-work") setTab("how");

    const quoteSummary = () => buyer.summary(state.quote);

    const updateQuoteButtons = () => {
      const selected = new Set(state.quote.map((item) => item.key));
      root.querySelectorAll("[data-rfq-add]").forEach((button) => {
        const isAdded = selected.has(button.dataset.rfqKey);
        button.classList.toggle("is-added", isAdded);
        button.textContent = isAdded ? text('added', 'Added to quote list') : (button.dataset.rfqKind === 'Dragflow pump family' ? text('shortlist', 'Shortlist family — specification help needed') : text('add', 'Add to quote list'));
        button.setAttribute("aria-pressed", String(isAdded));
      });
    };

    const renderQuote = () => {
      const hasItems = state.quote.length > 0;
      if (!hasItems && !rfqPanel.hidden) closeRfq();
      rfqTray.hidden = !hasItems;
      rfqCount.textContent = String(state.quote.length);
      rfqItems.innerHTML = state.quote.map((item, index) => `
        <div class="bizafrika-rfq-item">
          <div>
            <span>${escapeHtml(item.kind)}</span>
            <strong id="rfq-name-${index}">${escapeHtml(item.name)}</strong>
            ${item.ref ? `<small>${escapeHtml(item.ref)}</small>` : ""}
            <label class="bizafrika-rfq-item__quantity" for="rfq-quantity-${index}">${escapeHtml(text('quantity', 'Quantity'))}
              <input id="rfq-quantity-${index}" aria-describedby="rfq-name-${index}" type="number" min="1" max="9999" step="1" inputmode="numeric" data-rfq-quantity="${escapeHtml(item.key)}" value="${item.quantity ?? ''}" placeholder="${escapeHtml(text('unknown', 'Not yet known'))}">
            </label>
            ${item.kind === 'Dragflow pump family' ? `<small>${escapeHtml(text('family_note', 'Family shortlist only — no model selected.'))}</small>` : ''}
          </div>
          <button type="button" data-rfq-remove="${escapeHtml(item.key)}" aria-label="Remove ${escapeHtml(item.name)} from RFQ">Remove</button>
        </div>
      `).join("");
      if (rfqContactLink) {
        const contactUrl = new URL(root.dataset.contactUrl || rfqContactLink.href, window.location.origin);
        contactUrl.searchParams.set("rfq", "1");
        rfqContactLink.href = `${contactUrl.pathname}${contactUrl.search}${contactUrl.hash}`;
      }
      updateQuoteButtons();
    };

    const persistQuote = () => {
      const saved = saveQuote(state.quote);
      if (storageWarning) storageWarning.hidden = saved;
    };
    root.addEventListener('click', event => {
      if (!event.target.closest('[data-rfq-contact], [data-rfq-copy]')) return;
      const invalid = [...rfqItems.querySelectorAll('[data-rfq-quantity]')].find(input => !input.checkValidity());
      if (invalid) { event.preventDefault(); event.stopPropagation(); invalid.reportValidity(); }
    }, true);
    const updateQuantity = event => {
      const input = event.target.closest('[data-rfq-quantity]');
      if (!input) return;
      if (!input.checkValidity()) { if (event.type === 'change') input.reportValidity(); return; }
      const item = state.quote.find(entry => entry.key === input.dataset.rfqQuantity);
      if (!item) return;
      item.quantity = buyer.quantity(input.value);
      persistQuote();
      // The CRM form reads the saved shortlist on the contact page. Preserve focus here.
    };
    root.addEventListener('input', updateQuantity);
    root.addEventListener('change', updateQuantity);

    const addToQuote = (button) => {
      const item = {
        key: button.dataset.rfqKey,
        kind: button.dataset.rfqKind,
        name: button.dataset.rfqName,
        ref: button.dataset.rfqRef,
        quantity: null,
      };
      if (state.quote.some((existing) => existing.key === item.key)) {
        state.quote = state.quote.filter((existing) => existing.key !== item.key);
      } else {
        if (state.quote.length >= 100) return;
        state.quote.push(item);
      }
      persistQuote();
      renderQuote();
      rfqFeedback.textContent = state.quote.some((existing) => existing.key === item.key)
        ? `${item.name} added to the RFQ.`
        : `${item.name} removed from the RFQ.`;
    };

    root.addEventListener("click", (event) => {
      const addButton = event.target.closest("[data-rfq-add]");
      if (addButton) addToQuote(addButton);

      const removeButton = event.target.closest("[data-rfq-remove]");
      if (removeButton) {
        state.quote = state.quote.filter((item) => item.key !== removeButton.dataset.rfqRemove);
        persistQuote();
        renderQuote();
      }
    });

    let lastRfqTrigger = rfqToggle;

    const focusableRfqElements = () => [...rfqPanel.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )].filter((element) => element.getClientRects().length > 0);

    function openRfq() {
      lastRfqTrigger = document.activeElement instanceof HTMLElement ? document.activeElement : rfqToggle;
      rfqToggle.setAttribute("aria-expanded", "true");
      rfqPanel.hidden = false;
      document.body.classList.add("bizafrika-rfq-open");
      window.requestAnimationFrame(() => (rfqClose || rfqPanel).focus());
    }

    function closeRfq() {
      rfqToggle.setAttribute("aria-expanded", "false");
      rfqPanel.hidden = true;
      document.body.classList.remove("bizafrika-rfq-open");
      (lastRfqTrigger || rfqToggle).focus();
    }

    rfqToggle.addEventListener("click", () => {
      if (rfqPanel.hidden) openRfq();
      else closeRfq();
    });
    rfqClose.addEventListener("click", closeRfq);
    rfqPanel.addEventListener("keydown", (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeRfq();
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = focusableRfqElements();
      if (!focusable.length) {
        event.preventDefault();
        rfqPanel.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
    root.querySelector("[data-rfq-copy]").addEventListener("click", async () => {
      const summary = quoteSummary();
      try {
        await navigator.clipboard.writeText(summary);
      } catch (_error) {
        const textarea = document.createElement("textarea");
        textarea.value = summary;
        textarea.setAttribute("readonly", "");
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        textarea.remove();
      }
      rfqFeedback.textContent = "RFQ summary copied. Choose Request a quote to submit your shortlist to the BizAfrika team.";
    });

    const renderDragflow = () => {
      if (!state.dragflow) return;
      const query = dragflowSearch.value.trim().toLowerCase();
      const category = dragflowCategory.value;
      const families = state.dragflow.families.filter((family) => {
        const categoryMatch = category === "all" || family.category === category;
        const searchable = ["Dragflow pumps dredging slurry", family.model, family.category, ...family.variants.flatMap((variant) => [variant.model, variant.type])];
        return categoryMatch && matchesProductQuery(searchable, query);
      });
      dragflowCount.textContent = `${families.length} of ${state.dragflow.familyCount} pump families shown`;
      if (families.length === 0) {
        dragflowGrid.innerHTML = recovery('No pump families match this search. Try a model such as EL60 or HY85, or ask for selection help.', 'Dragflow', dragflowSearch.value, category === 'all' ? '' : category);
        return 0;
      }
      dragflowGrid.innerHTML = families.map((family) => {
        const imageUrl = safeHttpUrl(family.imageUrl);
        const sourceUrl = safeHttpUrl(family.sourceUrl);
        const first = family.variants[0] || {};
        const typeClass = family.category === "Hydraulic pumps" ? "hydraulic" : "electric";
        return `
          <article class="bizafrika-product-card bizafrika-pump-card">
            <div class="bizafrika-pump-card__media">
              ${imageUrl ? `<img src="${imageUrl}" alt="Dragflow ${escapeHtml(family.model)} pump" loading="lazy" width="640" height="480">` : '<div class="bizafrika-pump-card__placeholder">Dragflow</div>'}
              <span class="bizafrika-status bizafrika-status--${typeClass}">${escapeHtml(family.category)}</span>
            </div>
            <div class="bizafrika-product-card__body">
              <div class="bizafrika-product-card__topline"><span>Dragflow</span><span>${family.variants.length} model${family.variants.length === 1 ? "" : "s"}</span></div>
              <h3>${escapeHtml(family.model)}</h3>
              <p class="bizafrika-product-card__description">Heavy-duty ${typeClass} dredging pump family for abrasive slurries and demanding industrial applications.</p>
              <p class="bizafrika-model-row__muted">${escapeHtml(text('example', 'Example configuration:'))} ${escapeHtml(first.model || family.model)}</p>
              <dl class="bizafrika-spec-grid">
                ${specification("Capacity", first.capacity)}
                ${specification("Head", first.head)}
                ${specification("Power", first.power)}
                ${specification("Delivery", first.delivery)}
              </dl>
              <details class="bizafrika-models">
                <summary>View models and specifications</summary>
                <div class="bizafrika-models__list">
                  ${family.variants.map((variant) => {
                    const datasheet = safeHttpUrl(variant.datasheetUrl);
                    return `
                      <div class="bizafrika-model-row">
                        <div class="bizafrika-model-row__heading">
                          <div><strong>${escapeHtml(variant.model)}</strong><span>${escapeHtml(variant.type || "Pump configuration")}</span></div>
                          <button type="button" class="bizafrika-rfq-add" data-rfq-add data-rfq-key="dragflow-${escapeHtml(variant.model)}" data-rfq-kind="Dragflow pump" data-rfq-name="${escapeHtml(variant.model)}" data-rfq-ref="${escapeHtml(family.model)} family" aria-pressed="false">Add to RFQ</button>
                        </div>
                        <dl class="bizafrika-spec-grid bizafrika-spec-grid--compact">
                          ${specification("Capacity", variant.capacity)}
                          ${specification("Head", variant.head)}
                          ${specification("Power", variant.power)}
                          ${specification("Solids", variant.solidHandling)}
                          ${specification("Weight", variant.weight)}
                          ${specification("Speed", variant.speed ? `${variant.speed} rpm` : null)}
                        </dl>
                        ${datasheet ? `<a class="bizafrika-model-row__link" href="${datasheet}" target="_blank" rel="noopener">Technical datasheet ↗</a>` : '<span class="bizafrika-model-row__muted">Datasheet confirmed during quotation</span>'}
                      </div>
                    `;
                  }).join("")}
                </div>
              </details>
              <div class="bizafrika-product-card__footer">
                <button type="button" class="button" data-rfq-add data-rfq-key="dragflow-family-${escapeHtml(family.slug)}" data-rfq-kind="Dragflow pump family" data-rfq-name="${escapeHtml(family.model)}" data-rfq-ref="${escapeHtml(family.category)}" aria-pressed="false">Add family to RFQ</button>
                ${sourceUrl ? `<a href="${sourceUrl}" target="_blank" rel="noopener">Manufacturer reference ↗</a>` : ""}
              </div>
            </div>
          </article>
        `;
      }).join("");
      updateQuoteButtons();
      return families.length;
    };

    const renderEaton = () => {
      if (!state.eaton) return;
      const query = eatonSearch.value.trim().toLowerCase();
      const category = eatonCategory.value;
      const status = eatonStatus.value;
      const filtered = state.eaton.items.filter((item) => {
        const categoryMatch = category === "all" || item.category === category;
        const statusMatch = status === "all" || item.status === status;
        const searchable = ["Eaton electrical", item.title, item.partNumber, item.stockCode, item.category, item.sourceGroup];
        return categoryMatch && statusMatch && matchesProductQuery(searchable, query);
      });
      const visible = filtered.slice(0, state.eatonVisible);
      eatonCount.textContent = `${filtered.length} matching item${filtered.length === 1 ? "" : "s"}; showing ${visible.length}`;
      eatonLoadMore.hidden = visible.length >= filtered.length;
      eatonLoadMore.textContent = `Load ${Math.min(pageSize, filtered.length - visible.length)} more items`;
      if (visible.length === 0) {
        eatonGrid.innerHTML = recovery('No Eaton items match these filters. Try a part number, stock code or broader category.', 'Eaton', eatonSearch.value, category === 'all' ? 'Electrical and power' : customerFacingEatonCategory(category), status === 'all' ? '' : customerFacingEatonStatus(status));
        return 0;
      }
      eatonGrid.innerHTML = visible.map((item) => `
        <article class="bizafrika-product-card bizafrika-eaton-card">
          <div class="bizafrika-product-card__body">
            <div class="bizafrika-product-card__topline"><span>Eaton</span><span>${escapeHtml(customerFacingEatonCategory(item.category))}</span></div>
            <span class="bizafrika-status bizafrika-status--${escapeHtml(item.status)}">${escapeHtml(customerFacingEatonStatus(item.status))}</span>
            <h3>${escapeHtml(item.title)}</h3>
            <p class="bizafrika-eaton-card__category">${escapeHtml(customerFacingEatonCategory(item.category))}</p>
            <dl class="bizafrika-eaton-card__refs">
              <div><dt>Part / model</dt><dd>${escapeHtml(item.partNumber)}</dd></div>
              <div><dt>BizAfrika stock code</dt><dd>${escapeHtml(item.stockCode)}</dd></div>
            </dl>
            <p class="bizafrika-eaton-card__note">Price, exact specification and current availability are confirmed on quotation.</p>
            <button type="button" class="button" data-rfq-add data-rfq-key="eaton-${escapeHtml(item.id)}" data-rfq-kind="Eaton item" data-rfq-name="${escapeHtml(item.title)}" data-rfq-ref="${escapeHtml(item.partNumber)} / ${escapeHtml(item.stockCode)}" aria-pressed="false">Add to RFQ</button>
          </div>
        </article>
      `).join("");
      updateQuoteButtons();
      return filtered.length;
    };

    const resetEaton = () => {
      state.eatonVisible = pageSize;
      renderEaton();
    };
    dragflowSearch.addEventListener("input", renderDragflow);
    dragflowCategory.addEventListener("change", renderDragflow);
    eatonSearch.addEventListener("input", resetEaton);
    eatonCategory.addEventListener("change", resetEaton);
    eatonStatus.addEventListener("change", resetEaton);
    eatonLoadMore.addEventListener("click", () => {
      state.eatonVisible += pageSize;
      renderEaton();
    });

    renderQuote();

    const fetchJson = async (url) => {
      const response = await fetch(url, { credentials: "same-origin" });
      if (!response.ok) throw new Error(`Catalogue asset returned ${response.status}`);
      return response.json();
    };

    const [dragflowResult, eatonResult] = await Promise.allSettled([
      fetchJson(root.dataset.dragflowUrl),
      fetchJson(root.dataset.eatonUrl),
    ]);

    const itemQuery = (new URLSearchParams(window.location.search).get("item_search") || "").trim().slice(0, 100);
    if (itemQuery) {
      dragflowSearch.value = itemQuery;
      eatonSearch.value = itemQuery;
    }
    let dragflowMatches = 0;
    let eatonMatches = 0;

    if (dragflowResult.status === "fulfilled") {
      state.dragflow = dragflowResult.value;
      dragflowMatches = renderDragflow();
    } else {
      dragflowCount.textContent = "Dragflow catalogue could not be loaded.";
      dragflowGrid.innerHTML = recovery('The pump catalogue is temporarily unavailable. You can still request product guidance.', 'Dragflow', itemQuery);
    }

    if (eatonResult.status === "fulfilled") {
      state.eaton = eatonResult.value;
      state.eaton.categories.forEach((category) => {
        const option = document.createElement("option");
        option.value = category;
        option.textContent = customerFacingEatonCategory(category);
        eatonCategory.appendChild(option);
      });
      eatonMatches = renderEaton();
    } else {
      eatonCount.textContent = "Eaton catalogue could not be loaded.";
      eatonGrid.innerHTML = recovery('The Eaton catalogue is temporarily unavailable. You can still request product guidance.', 'Eaton', itemQuery);
    }

    if (itemQuery) {
      setTab(eatonMatches > 0 && !dragflowMatches ? "eaton" : "dragflow", true);
      root.querySelector("#detailed-ranges")?.scrollIntoView({ behavior: "auto", block: "start" });
    }
  };

  const initializeAll = () => document.querySelectorAll("[data-bizafrika-catalogue]").forEach((root) => {
    initializeCatalogue(root).catch(() => {
      const loading = root.querySelectorAll(".bizafrika-catalogue__loading");
      loading.forEach((element) => {
        element.textContent = "The catalogue could not initialize. Please refresh the page or contact BizAfrika.";
      });
    });
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initializeAll, { once: true });
  } else {
    initializeAll();
  }
  document.addEventListener("shopify:section:load", initializeAll);
})();
